import { Store } from 'pullstate';
import { MorphologyClue, PuzzleStoreBase, PuzzleStoreBaseDefaults } from '../../../utils/types';

export type MorphologyStoreType = PuzzleStoreBase & {
  // Unlike every other format implemented so far, morphology can be marked 'revealed'
  // without being finished, because the user can reveal individual cells, which still
  // counts as revealing for purposes of solve streaks & the solve banner on the main page.
  //
  // So we use this `completed` boolean to indicate whether the puzzle is done or not
  // instead of relying on solved/revealed, since it can be partially revealed but not done.
  // This attribute is what's set to true when the last cell is revealed or guessed correctly,
  // and triggers the solve animation.
  //
  // In hindsight, perhaps I should have written this as "if solved and not revealed,
  // show solved banner and increment streak" instead of adding an entire additional boolean.
  // It doesn't really matter that solved animations rely on 'solved'. they don't trigger when
  // you reveal all and we don't care if they trigger when you reveal some. sigh.
  completed: boolean;
  author: string;
  // True for cryptic-clue morphologies (a special, streak-optional variant).
  cryptic: boolean;
  clues: MorphologyClue[],
  guesses: (string | null)[][];
  // tracks which cells are revealed
  revealedCells: boolean[][];
  // If a cell is "Checked" and it's right, it's added to the reveals.
  // But if it's wrong, it's added to mistakes and marked with an X until it's changed again.
  mistakes: boolean[][];
  toolbar: {
    almostThereIsOpen: boolean;
    aboutIsOpen: boolean;
    clearIsOpen: boolean;
    checkIsOpen: boolean;
    revealIsOpen: boolean;
    // this is the combined dropdown for mobile; clear+check+reveal
    combinedIsOpen: boolean;
    confirmationDialog: {
      clearAllIsOpen: boolean;
      checkLetterIsOpen: boolean;
      checkWordIsOpen: boolean;
      checkPuzzleIsOpen: boolean;
      autocheckIsOpen: boolean;
      revealLetterIsOpen: boolean;
      revealWordIsOpen: boolean;
      revealAllIsOpen: boolean;
    };
  };
  focusedClueIndex: number;
  cursorIndex: number;
  autocheckOn: boolean;
};

export const MorphologyStore = new Store<MorphologyStoreType>({
  ...PuzzleStoreBaseDefaults,
  completed: false,
  author: '',
  cryptic: false,
  clues: [],
  guesses: [],
  revealedCells: [],
  mistakes: [],
  toolbar: {
    almostThereIsOpen: false,
    aboutIsOpen: false,
    clearIsOpen: false,
    checkIsOpen: false,
    revealIsOpen: false,
    combinedIsOpen: false,
    confirmationDialog: {
      clearAllIsOpen: false,
      checkLetterIsOpen: false,
      checkWordIsOpen: false,
      checkPuzzleIsOpen: false,
      autocheckIsOpen: false,
      revealLetterIsOpen: false,
      revealWordIsOpen: false,
      revealAllIsOpen: false,
    },
  },
  focusedClueIndex: 0,
  cursorIndex: 0,
  autocheckOn: false,
});